"use client";

import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ThreadListPrimitive } from "@assistant-ui/react";
import {
  MessageSquareIcon,
  PanelLeftIcon,
  PlusIcon,
  SearchIcon,
} from "lucide-react";
import { ThreadList } from "@/components/assistant-ui/thread-list";
import { SidebarUser } from "@/components/assistant-ui/sidebar-user";
import { WebViewBackButton } from "@/components/custom/webviewBackButton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { appendLocParam } from "@/lib/i18n";
import { useAuth } from "@/lib/auth/AuthContext";

const STORAGE_KEY = "praetor.sidebar.collapsed";

export function ThreadListSidebar({
  className,
  onOpenSearch,
  ...props
}: React.HTMLAttributes<HTMLElement> & { onOpenSearch?: () => void }) {
  // SSR + first paint render expanded; once mounted we hydrate from
  // localStorage to avoid an FOUC flicker on cold load.
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") setCollapsed(true);
    } catch {
      /* localStorage may be blocked; stay expanded */
    }
  }, []);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <aside
      data-collapsed={collapsed || undefined}
      className={cn(
        "praetor-sidebar group/sidebar sticky top-0 flex h-screen shrink-0 flex-col border-border/60 border-r bg-sidebar",
        "transition-[width] duration-220 ease-[cubic-bezier(.4,0,.2,1)]",
        collapsed ? "w-[56px]" : "w-[264px]",
        className,
      )}
      {...props}
    >
      <SidebarHeader collapsed={collapsed} onToggle={toggle} />
      <SidebarBody collapsed={collapsed} onOpenSearch={onOpenSearch} />
      <SidebarUser collapsed={collapsed} />
    </aside>
  );
}

function SidebarHeader({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const { scheme } = useAuth();
  const showWebViewBack = scheme === "Bearer";

  return (
    <header
      className={cn(
        "praetor-sidebar-header flex h-[56px] items-center pt-3.5 pb-2",
        collapsed
          ? showWebViewBack
            ? "justify-center gap-0.5 px-1"
            : "justify-center px-0"
          : showWebViewBack
            ? "justify-between pr-3 pl-3"
            : "justify-end pr-3 pl-4",
      )}
    >
      {showWebViewBack && (
        <WebViewBackButton mode="sidebar" collapsed={collapsed} />
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              "flex cursor-pointer items-center justify-center rounded-[7px] text-muted-foreground transition-colors duration-120 hover:bg-muted hover:text-foreground",
              collapsed && showWebViewBack ? "size-[26px]" : "size-[30px]",
            )}
            aria-label={t("sidebar.toggle")}
          >
            <PanelLeftIcon className="size-4" strokeWidth={1.7} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{t("sidebar.toggle")}</TooltipContent>
      </Tooltip>
    </header>
  );
}

function SidebarBody({ collapsed, onOpenSearch }: { collapsed: boolean; onOpenSearch?: () => void }) {
  return (
    <div
      className="praetor-sidebar-body flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pt-1 pb-3"
      style={{ scrollbarGutter: "stable" }}
    >
      <TopActions collapsed={collapsed} onOpenSearch={onOpenSearch} />
      {!collapsed && <ThreadList />}
    </div>
  );
}

function TopActions({ collapsed, onOpenSearch }: { collapsed: boolean; onOpenSearch?: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const onChatsRoute = pathname.startsWith("/chats");
  const onSettingsRoute = pathname.startsWith("/settings");
  const routeContext = useRouteContext();
  const navigate = useNavigate();

  // Both handlers route through react-router's navigate() so the SPA route
  // state stays in sync with the URL bar. Earlier versions used
  // window.history.pushState here; that desynced the router (its internal
  // pathname stayed at /chats while the URL bar showed /default), which
  // caused subsequent link clicks to be silently no-op'd by the router
  // because it thought we were already there. navigate() forces a real
  // route transition; the destination's RouteContextWrapper re-mounts with
  // fresh state from URL params, so the inline routeContext mutations in
  // the old handlers are no longer needed.
  const handleNewThreadFromChats = useCallback(() => {
    const dest = routeContext.launchCaseId ?? "default";
    navigate(appendLocParam(`/${dest}`));
  }, [navigate, routeContext.launchCaseId]);

  const handleReturnToConversation = useCallback(() => {
    const threadId = routeContext.threadId;
    if (!threadId) {
      navigate(appendLocParam("/default"));
      return;
    }
    // Bug fix in passing: previous code emitted `/${threadId}` which matched
    // the [caseId] segment with caseId=<threadId> — wrong. Always
    // include the case segment so we land on [caseId]/[threadId].
    const caseIdSegment = routeContext.caseId ?? "default";
    navigate(appendLocParam(`/${caseIdSegment}/${threadId}`));
  }, [routeContext.threadId, routeContext.caseId, navigate]);

  return (
    <>
      {onChatsRoute || onSettingsRoute ? (
        <ActionRow
          icon={PlusIcon}
          label={t("sidebar.newChat")}
          collapsed={collapsed}
          onClick={handleNewThreadFromChats}
        />
      ) : (
        <ThreadListPrimitive.New asChild>
          <ActionRow
            icon={PlusIcon}
            label={t("sidebar.newChat")}
            collapsed={collapsed}
          />
        </ThreadListPrimitive.New>
      )}
      {onChatsRoute || onSettingsRoute ? (
        <ActionRow
          icon={MessageSquareIcon}
          label={t("sidebar.chats")}
          collapsed={collapsed}
          onClick={onSettingsRoute ? undefined : handleReturnToConversation}
          href={onSettingsRoute ? "/chats" : undefined}
        />
      ) : (
        <ActionRow
          icon={MessageSquareIcon}
          label={t("sidebar.chats")}
          collapsed={collapsed}
          href="/chats"
        />
      )}
      <ActionRow
        icon={SearchIcon}
        label={t("sidebar.search")}
        collapsed={collapsed}
        onClick={onOpenSearch}
      />
    </>
  );
}

type ActionRowProps = {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  collapsed: boolean;
  href?: string;
  active?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">;

const ActionRow = React.forwardRef<HTMLElement, ActionRowProps>(
  function ActionRow(
    { icon: Icon, label, collapsed, href, active, className, ...rest },
    ref,
  ) {
    const baseClass = cn(
      "praetor-sb-row flex items-center rounded-lg font-medium text-[13.5px] transition-colors duration-120",
      collapsed
        ? "mx-auto size-[40px] justify-center p-2"
        : "w-full gap-2.5 px-2.5 py-1.5",
      active
        ? "bg-accent/40 text-foreground"
        : "cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground",
      className,
    );

    const inner = (
      <>
        <Icon className="size-4 shrink-0" strokeWidth={1.7} />
        {!collapsed && (
          <span className="flex-1 truncate text-left">{label}</span>
        )}
      </>
    );

    const element = href ? (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={href}
        className={baseClass}
        aria-current={active ? "page" : undefined}
      >
        {inner}
      </Link>
    ) : (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={baseClass}
        {...rest}
      >
        {inner}
      </button>
    );

    if (!collapsed) return element;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{element}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  },
);
